import { ExternalLink, FileText, Video, BookOpen, Shield, Wrench } from "lucide-react";

type ResourceType = "Datasheet" | "Whitepaper" | "Guide" | "Webinar" | "Tool" | "Solution Brief";

interface Resource {
  title: string;
  source: string;
  type: ResourceType;
  description: string;
  url: string;
  sponsored?: boolean;
}

const typeIcons: Record<ResourceType, React.ReactNode> = {
  Datasheet: <FileText size={14} />,
  Whitepaper: <BookOpen size={14} />,
  Guide: <BookOpen size={14} />,
  Webinar: <Video size={14} />,
  Tool: <Wrench size={14} />,
  "Solution Brief": <Shield size={14} />,
};

const typeColors: Record<ResourceType, string> = {
  Datasheet: "bg-primary/10 text-primary",
  Whitepaper: "bg-accent/20 text-accent-foreground",
  Guide: "bg-secondary text-secondary-foreground",
  Webinar: "bg-destructive/10 text-destructive",
  Tool: "bg-muted text-muted-foreground",
  "Solution Brief": "bg-primary/10 text-primary",
};

const resources: Resource[] = [
  {
    title: "NERC CIP Compliance — Cyber Security Standards",
    source: "ServiceAide",
    type: "Datasheet",
    description: "How ServiceAide's platform supports NERC CIP v5 cyber security standards across change management, incident response, and asset management.",
    url: "https://www.serviceaide.com/data-sheet/nerc-cip-compliance-v5-cyber-security-standards",
    sponsored: true,
  },
  {
    title: "OT Cybersecurity Solutions for NERC CIP",
    source: "Dragos",
    type: "Solution Brief",
    description: "How the Dragos Platform and professional services support NERC CIP compliance for OT/ICS environments across electric utilities.",
    url: "https://www.dragos.com/resources/solution-brief/ot-cybersecurity-solutions-for-nerc-cip/",
  },
  {
    title: "OT Network Visibility & Detective Controls in a NERC CIP World",
    source: "Dragos",
    type: "Whitepaper",
    description: "Deep dive into achieving OT network visibility and implementing detective controls aligned with NERC CIP requirements.",
    url: "https://www.dragos.com/resources/whitepaper/achieving-ot-network-visibility-and-detective-controls-in-a-nerc-cip-world/",
  },
  {
    title: "Supporting NERC CIP Compliance",
    source: "Claroty",
    type: "Datasheet",
    description: "How Claroty supports NERC CIP reliability guidelines with asset discovery, network protection, and threat detection for critical infrastructure.",
    url: "https://claroty.com/resources/datasheets/supporting-nerc-cip-compliance",
  },
  {
    title: "OT Cybersecurity & NERC CIP for Public Power Utilities",
    source: "Claroty",
    type: "Datasheet",
    description: "Tailored guidance for public power utilities navigating OT cybersecurity and NERC CIP compliance obligations.",
    url: "https://claroty.com/resources/datasheets/ot-cybersecurity-and-nerc-cip-compliance-for-public-power-utilities",
  },
  {
    title: "NERC CIP Critical Infrastructure Protection Roadmap",
    source: "NERC",
    type: "Whitepaper",
    description: "The official 2026 NERC CIP Roadmap outlining priorities for INSM, low-impact protections, cloud governance, and supply chain security.",
    url: "https://www.nerc.com/globalassets/our-work/reports/special-reports/nerc_cip_roadmap_01122026.pdf",
  },
  {
    title: "CIP Evidence Request Tool User Guide",
    source: "NERC",
    type: "Tool",
    description: "Official guide for using NERC's CIP Evidence Request Tool, essential for audit preparation and compliance evidence management.",
    url: "https://www.nerc.com/globalassets/programs/compliance/cip-evidence-request-tool-user-guide-v9.pdf",
  },
  {
    title: "5 Key Concepts for NERC CIP Compliance",
    source: "OPSWAT",
    type: "Guide",
    description: "Practical overview of protecting critical OT environments and maintaining NERC CIP compliance with media transfer and access controls.",
    url: "https://www.opswat.com/blog/5-key-concepts-to-consider-for-nerc-cip-compliance",
  },
  {
    title: "NERC CIP 2025: Zero Trust OT Security for Co-ops & Municipals",
    source: "Xage",
    type: "Guide",
    description: "How cooperative and municipal utilities can strengthen cybersecurity under new NERC CIP mandates using zero trust architecture.",
    url: "https://xage.com/blog/strengthening-cybersecurity-for-co-op-and-municipal-utilities-under-new-nerc-cip-mandates/",
  },
  {
    title: "NERC CIP Compliance Datasheet",
    source: "RSI Security",
    type: "Datasheet",
    description: "Overview of RSI Security's NERC CIP assessment and compliance services for power generation, transmission, and distribution organizations.",
    url: "https://www.rsisecurity.com/nerc-cip-datasheet/",
  },
  {
    title: "ERO Enterprise 2026 CMEP Implementation Plan",
    source: "NERC",
    type: "Whitepaper",
    description: "The official 2026 compliance monitoring plan covering risk elements, regional priorities, and oversight strategy for the ERO Enterprise.",
    url: "https://www.nerc.com/pa/comp/Pages/default.aspx",
  },
  {
    title: "NERC CIP Compliance Checklist for Critical Infrastructure",
    source: "Network Intelligence",
    type: "Guide",
    description: "Step-by-step compliance checklist covering all major CIP standards with practical implementation guidance.",
    url: "https://www.networkintelligence.ai/blogs/nerc-cip-compliance-checklists/",
  },
];

const ResourcesSection = () => {
  return (
    <section className="border-t border-border bg-secondary/30">
      <div className="container max-w-6xl py-10 px-4">
        <div className="flex items-center gap-3 mb-2">
          <span className="text-primary"><BookOpen size={16} /></span>
          <h2 className="text-sm font-bold text-foreground uppercase tracking-wider">Resources</h2>
          <div className="flex-1 h-px bg-border" />
          <span className="text-xs font-mono text-muted-foreground bg-secondary px-2 py-0.5 rounded">
            {resources.length}
          </span>
        </div>
        <p className="text-xs text-muted-foreground mb-6 max-w-lg">
          Curated datasheets, whitepapers, guides, and tools from across the NERC CIP ecosystem.
        </p>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {resources.map((resource, i) => (
            <a
              key={i}
              href={resource.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group block rounded-lg border border-border bg-card p-4 hover:border-primary/40 hover:shadow-sm transition-all"
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-medium ${typeColors[resource.type]}`}>
                  {typeIcons[resource.type]}
                  {resource.type}
                </span>
                {resource.sponsored && (
                  <span className="text-[9px] font-semibold text-primary uppercase tracking-wider">Sponsored</span>
                )}
              </div>
              <h3 className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors leading-snug mb-1.5">
                {resource.title}
              </h3>
              <p className="text-xs text-muted-foreground leading-relaxed mb-3 line-clamp-2">
                {resource.description}
              </p>
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-medium text-muted-foreground">{resource.source}</span>
                <ExternalLink size={12} className="text-muted-foreground group-hover:text-primary transition-colors" />
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ResourcesSection;
